import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { RecentItem } from './merge-recent-items';
import { getRecentsDateGroupForItem, type RecentsDateGroup } from './recents-date-group';

export const DEFAULT_COLLAPSED_RECENTS_DATE_GROUPS = new Set<RecentsDateGroup>();

type UseRecentsDateGroupCollapseParams = {
  currentChatId: string | null;
  typeFilteredItems: readonly RecentItem[];
  isSearching: boolean;
};

type UseRecentsDateGroupCollapseResult = {
  collapsedGroups: ReadonlySet<RecentsDateGroup>;
  isGroupCollapsed: (group: RecentsDateGroup) => boolean;
  setGroupOpen: (group: RecentsDateGroup, open: boolean) => void;
};

export const useRecentsDateGroupCollapse = ({
  currentChatId,
  typeFilteredItems,
  isSearching,
}: UseRecentsDateGroupCollapseParams): UseRecentsDateGroupCollapseResult => {
  const [collapsedGroups, setCollapsedGroups] = useState<ReadonlySet<RecentsDateGroup>>(
    () => new Set(DEFAULT_COLLAPSED_RECENTS_DATE_GROUPS)
  );
  const lastAutoExpandedChatIdRef = useRef<string | null>(null);
  const shouldScrollToActiveChatRef = useRef(false);

  const activeChatGroup = useMemo(() => {
    if (isSearching || !currentChatId) return null;

    const activeItem = typeFilteredItems.find(
      (item) => item.kind === 'chat' && item.chat.id === currentChatId
    );
    if (!activeItem) return null;

    return getRecentsDateGroupForItem(activeItem, new Date());
  }, [currentChatId, isSearching, typeFilteredItems]);

  const isGroupCollapsed = useCallback(
    (group: RecentsDateGroup) => collapsedGroups.has(group),
    [collapsedGroups]
  );

  const setGroupOpen = useCallback((group: RecentsDateGroup, open: boolean) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (open) next.delete(group);
      else next.add(group);
      const unchanged = next.size === prev.size && [...next].every((g) => prev.has(g));
      return unchanged ? prev : next;
    });
  }, []);

  useEffect(() => {
    if (!currentChatId) {
      lastAutoExpandedChatIdRef.current = null;
      return;
    }
    if (!activeChatGroup) return;
    if (lastAutoExpandedChatIdRef.current === currentChatId) return;

    lastAutoExpandedChatIdRef.current = currentChatId;
    shouldScrollToActiveChatRef.current = true;
    if (collapsedGroups.has(activeChatGroup)) {
      setCollapsedGroups((prev) => {
        if (!prev.has(activeChatGroup)) return prev;
        const next = new Set(prev);
        next.delete(activeChatGroup);
        return next;
      });
    }
  }, [activeChatGroup, collapsedGroups, currentChatId]);

  useEffect(() => {
    if (!shouldScrollToActiveChatRef.current || !currentChatId || !activeChatGroup)
      return;
    if (collapsedGroups.has(activeChatGroup)) return;

    const frame = requestAnimationFrame(() => {
      shouldScrollToActiveChatRef.current = false;
      document
        .querySelector(`[data-recent-chat-id="${currentChatId}"]`)
        ?.scrollIntoView({ block: 'nearest' });
    });
    return () => cancelAnimationFrame(frame);
  }, [activeChatGroup, collapsedGroups, currentChatId]);

  return {
    collapsedGroups,
    isGroupCollapsed,
    setGroupOpen,
  };
};
